import { action, computed, observable, makeObservable } from 'mobx';
import { GameSharedType } from './typesExt';
import { CategoryType, CollectionType, GameType, ProviderType } from 'src/domains/casino/types/types';
import { CollectionClientType, CustomerClientType, GameClient } from './http-clients/adapter';
import { RedirectState } from 'src/domains/casino/state/AppCasino/Redirect.state';
import { GameModalState } from 'src/domains/casino/state/AppCasino/game-modal/GameModal.state';

export class CasinoGameModelExt {
    @observable private isFavouriteOptimistic: boolean | null = null;
    @observable private addedToFavouritesConfirmation: boolean = false;
    private readonly collectionClient: CollectionClientType;
    private readonly customerClient: CustomerClientType;

    public constructor(
        public readonly collection: CollectionType,
        private readonly gameClient: GameClient,
        private readonly gameId: number,
        private readonly redirectState: RedirectState,
        private readonly gameModalState: GameModalState
    ) {
        makeObservable(this);
        this.collectionClient = this.gameClient.collectionMap.get(collection);
        this.customerClient = this.gameClient.customer;
    }

    /**
     * @deprecated - this getter should be private
     */
    @computed.struct public get data(): GameType | null {
        return this.collectionClient.categoriesAndGames.games.find((game) => game.id === this.gameId) ?? null;
    }

    @computed public get id(): number {
        return this.gameId;
    }

    @computed public get name(): string {
        return this.data?.name ?? '';
    }

    @computed public get imageHorizontal(): string {
        return this.data?.imageHorizontal ?? '';
    }

    @computed public get imageSquare(): string {
        return this.data?.imageSquare ?? '';
    }

    @computed public get imageVertical(): string {
        return this.data?.imageVertical ?? '';
    }

    @computed public get launchGameId(): string {
        return this.data?.providerGameId ?? '';
    }

    @computed public get studioId(): number | null {
        return this.data?.studioId ?? null;
    }

    @computed public get studioSlug(): string {
        return this.data?.studioSlug ?? '';
    }

    @computed public get studioName(): string {
        return this.data?.studioName ?? '';
    }

    @computed public get provider(): ProviderType | null {
        if (this.data === null) {
            return null;
        }
        return this.data.providerSlug;
    }

    @computed public get providerName(): string | null {
        if (this.data === null) {
            return null;
        }
        return this.data.providerName;
    }

    @computed public get firstCategory(): CategoryType | null {
        for (const category of this.collectionClient.categoriesAndGames.categories) {
            if (category.gameIds.includes(this.gameId)) {
                return category;
            }
        }

        return null;
    }

    @computed public get GameSharedType(): GameSharedType | null {
        if (this.data === null) {
            return null;
        }

        return {
            id: this.data.id,
            name: this.data.name,
        };
    }

    @computed public get isFavourite(): boolean {
        if (this.isFavouriteOptimistic !== null) {
            return this.isFavouriteOptimistic;
        }
        const { favouriteGamesIds } = this.customerClient;
        return favouriteGamesIds.includes(this.gameId);
    }

    public handleLikeAction = (): void => {
        if (this.redirectState.goToLoginForm()) {
            return;
        }
        const newFavourite = !this.isFavourite;
        this.isFavouriteOptimistic = newFavourite;

        this.customerClient.setFavouriteGame(this.gameId, newFavourite);

        if (newFavourite) {
            this.addedToFavouritesConfirmation = true;
            setTimeout(() => {
                this.addedToFavouritesConfirmation = false;
            }, 3000);
        }
    };

    public get isAddedToFavouritesConfirmation(): boolean {
        return this.addedToFavouritesConfirmation;
    }

    @action public startGame = (options?: { mode?: 'demo' | 'mini' | 'bingo-mini' }): void => {
        if (options?.mode === 'mini') {
            return this.gameModalState.startGame({
                type: 'model',
                gameModel: this,
                mode: options.mode,
            });
        }
        this.redirectState.redirectToCasinoGame(this.gameId, this.collection, options?.mode);

        if (this.collection !== 'virtuals') {
            this.customerClient.setRecentGame(this.gameId);
        }
    };
}
